// Postman Collection 格式适配器
// 参考: https://schema.getpostman.com/json/collection/v2.1.0/collection.json

/**
 * 将内部项目数据转换为 Postman Collection 格式
 */
export function toPostmanCollection(projects, options = {}) {
  const collection = {
    info: {
      _postman_id: generateId(),
      name: options.name || 'DAPI Collection',
      description: options.description || 'Exported from DAPI',
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json'
    },
    item: projects.map(project => convertNodeToPostman(project))
  }

  return collection
}

// 递归转换节点为 Postman 格式
function convertNodeToPostman(node) {
  // 如果是接口（有 method）
  if (node.method) {
    return {
      name: node.name,
      request: {
        method: node.method,
        header: convertHeaders(node.headers),
        url: convertUrl(node.url, node.params),
        body: convertBody(node.body),
        description: node.description
      },
      response: []
    }
  }
  
  // 如果是文件夹
  return {
    name: node.name,
    description: node.description,
    item: (node.children || []).map(child => convertNodeToPostman(child))
  }
}

/**
 * 将 Postman Collection 格式转换为内部项目数据
 * 支持多层嵌套结构
 */
export function fromPostmanCollection(collection) {
  if (!collection || !collection.item) {
    return []
  }

  // 使用 collection.info.name 作为项目名称
  const projectName = collection.info?.name || '导入的项目'
  const projectDescription = collection.info?.description || ''

  // 将所有 item 作为项目的 children
  return [{
    id: generateId(),
    type: 'project',
    name: projectName,
    description: projectDescription,
    children: collection.item.map(item => convertPostmanToNode(item, 'folder'))
  }]
}

// 递归转换 Postman 节点为内部格式
function convertPostmanToNode(item, type = 'folder') {
  // 如果有 request，说明是接口
  if (item.request) {
    const request = item.request
    return {
      id: generateId(),
      type: 'api',
      name: item.name || '未命名接口',
      method: request.method || 'GET',
      url: extractUrl(request.url),
      headers: parseHeaders(request.header),
      params: parseParams(request.url),
      body: parseBody(request.body),
      description: request.description || ''
    }
  }
  
  // 如果是文件夹或项目
  return {
    id: generateId(),
    type: type, // 'project' 或 'folder'
    name: item.name || '未命名',
    description: item.description || '',
    children: (item.item || []).map(child => convertPostmanToNode(child, 'folder'))
  }
}

// 转换 Headers
function convertHeaders(headers) {
  if (!headers || !Array.isArray(headers)) return []

  return headers
    .filter(h => h.key)
    .map(h => ({
      key: h.key,
      value: h.value || '',
      description: h.description || '',
      type: 'text'
    }))
}

// 解析 Headers
function parseHeaders(headers) {
  if (!headers || !Array.isArray(headers)) return [{ key: '', value: '', description: '' }]

  return headers.map(h => ({
    key: h.key || '',
    value: h.value || '',
    description: h.description || ''
  }))
}

// 转换 URL
function convertUrl(url, params) {
  if (!url) {
    return {
      raw: '',
      host: [''],
      path: [''],
      query: convertParams(params)
    }
  }

  // 解析 URL
  let raw = url
  let host = ['']
  let path = ['']
  
  try {
    const urlObj = new URL(url)
    host = [urlObj.host]
    path = urlObj.pathname.split('/').filter(p => p)
    raw = url.split('?')[0] // 移除 query string
  } catch (e) {
    // 相对路径或无效 URL
    path = url.split('/').filter(p => p)
  }

  return {
    raw: raw + (params && params.length > 0 ? '?' + params.filter(p => p.key).map(p => `${p.key}=${p.value}`).join('&') : ''),
    host: host,
    path: path,
    query: convertParams(params)
  }
}

// 提取 URL
function extractUrl(urlObj) {
  if (!urlObj) return ''
  
  if (typeof urlObj === 'string') {
    return urlObj
  }
  
  // 从 Postman URL 对象提取
  let url = ''
  if (urlObj.raw) {
    return urlObj.raw
  }
  
  if (urlObj.host && urlObj.host.length > 0) {
    url += urlObj.host.join('.')
  }
  
  if (urlObj.path && urlObj.path.length > 0) {
    url += '/' + urlObj.path.join('/')
  }
  
  return url
}

// 转换 Params
function convertParams(params) {
  if (!params || !Array.isArray(params)) return []

  return params
    .filter(p => p.key)
    .map(p => ({
      key: p.key,
      value: p.value || '',
      description: p.description || ''
    }))
}

// 解析 Params
function parseParams(urlObj) {
  if (!urlObj || !urlObj.query) return [{ key: '', value: '', description: '' }]

  return urlObj.query.map(q => ({
    key: q.key || '',
    value: q.value || '',
    description: q.description || ''
  }))
}

// 转换 Body
function convertBody(body) {
  if (!body || body.type === 'none') {
    return { mode: 'raw', raw: '' }
  }
  
  switch (body.type) {
    case 'json':
      return {
        mode: 'raw',
        raw: body.content || '',
        options: {
          raw: {
            language: 'json'
          }
        }
      }
    case 'form':
      return {
        mode: 'urlencoded',
        urlencoded: parseFormData(body.content)
      }
    case 'raw':
    default:
      return {
        mode: 'raw',
        raw: body.content || ''
      }
  }
}

// 解析 Body
function parseBody(bodyObj) {
  if (!bodyObj) {
    return { type: 'none', content: '' }
  }
  
  const mode = bodyObj.mode || 'raw'
  
  switch (mode) {
    case 'urlencoded':
    case 'formdata':
      return {
        type: 'form',
        content: stringifyFormData(bodyObj.urlencoded || bodyObj.formdata)
      }
    case 'raw': {
      // 检查是否是 JSON
      const raw = bodyObj.raw || ''
      try {
        JSON.parse(raw)
        return {
          type: 'json',
          content: raw
        }
      } catch {
        return {
          type: 'raw',
          content: raw
        }
      }
    }
    default:
      return {
        type: 'raw',
        content: bodyObj.raw || ''
      }
  }
}

// 解析表单数据
function parseFormData(content) {
  if (!content) return []
  
  try {
    const pairs = content.split('&')
    return pairs.map(pair => {
      const [key, value] = pair.split('=')
      return { key: decodeURIComponent(key || ''), value: decodeURIComponent(value || '') }
    })
  } catch {
    return []
  }
}

// 字符串化表单数据
function stringifyFormData(data) {
  if (!data || !Array.isArray(data)) return ''
  
  return data
    .filter(item => item.key)
    .map(item => `${encodeURIComponent(item.key)}=${encodeURIComponent(item.value || '')}`)
    .join('&')
}

// 生成唯一 ID
function generateId() {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15)
}

// 导出为文件
export function downloadAsFile(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// 从文件导入
export function importFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result)
        resolve(data)
      } catch (error) {
        reject(new Error('无效的 JSON 文件'))
      }
    }
    reader.onerror = () => reject(new Error('读取文件失败'))
    reader.readAsText(file)
  })
}

// 递归查找节点
export function findNodeById(nodes, id) {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }
    if (node.children) {
      const found = findNodeById(node.children, id)
      if (found) return found
    }
  }
  return null
}

// 递归删除节点
export function removeNodeById(nodes, id) {
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].id === id) {
      nodes.splice(i, 1)
      return true
    }
    if (nodes[i].children) {
      if (removeNodeById(nodes[i].children, id)) {
        return true
      }
    }
  }
  return false
}

// 获取所有接口（扁平化）
export function getAllApis(node, result = []) {
  if (node.type === 'api') {
    result.push(node)
  }
  if (node.children) {
    for (const child of node.children) {
      getAllApis(child, result)
    }
  }
  return result
}
